import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Stack,
  Group,
  Title,
  Text,
  Button,
  Badge,
  Table,
  Alert,
  Skeleton,
  Modal,
  TagsInput,
  Select,
  NumberInput,
  Switch,
  ActionIcon,
  Tooltip,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import {
  IconPlus,
  IconSend,
  IconPencil,
  IconTrash,
  IconMailFast,
} from '@tabler/icons-react'
import { useCan, useUserTenants } from '@/store/auth'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import {
  listReportSubscriptions,
  createReportSubscription,
  patchReportSubscription,
  deleteReportSubscription,
  sendReportNow,
  getReportRuns,
  type FinopsReportSubscription,
  type FinopsReportFrequency,
  type FinopsReportFormat,
  type FinopsReportRunStatus,
} from '@/api/finops'

const FREQUENCY_LABEL: Record<FinopsReportFrequency, string> = {
  weekly: 'Semanal',
  monthly: 'Mensal',
}

const FORMAT_LABEL: Record<FinopsReportFormat, string> = {
  pdf: 'PDF',
  csv: 'CSV',
  xls: 'XLS',
}

/** Cor do Badge por status de run (tokens DS via cores nomeadas Mantine). */
function runStatusColor(status: FinopsReportRunStatus): string {
  switch (status) {
    case 'success':
    case 'sent':
      return 'green'
    case 'failed':
      return 'red'
    case 'skipped':
      return 'gray'
    case 'running':
      return 'blue'
    default:
      return 'yellow' // pending
  }
}

const RUN_STATUS_LABEL: Record<FinopsReportRunStatus, string> = {
  pending: 'Na fila',
  running: 'Executando',
  success: 'Concluída',
  sent: 'Enviada',
  failed: 'Falhou',
  skipped: 'Ignorada',
}

/** Célula com o status da última run da assinatura (lazy por linha). */
function LastRunCell({ subscriptionId }: { subscriptionId: number }) {
  const { data, isLoading } = useQuery({
    queryKey: ['finops-sub-runs', subscriptionId],
    queryFn: () => getReportRuns(subscriptionId, 1),
    staleTime: 30_000,
  })
  if (isLoading) return <Skeleton height={18} width={90} />
  const last = data?.[0]
  if (!last) return <Text c="dimmed" size="sm">—</Text>
  return (
    <Group gap="xs" wrap="nowrap">
      <Badge variant="light" color={runStatusColor(last.status)}>
        {RUN_STATUS_LABEL[last.status] ?? last.status}
      </Badge>
      <Text size="xs" c="dimmed">
        {new Date(last.scheduled_for).toLocaleString('pt-BR')}
      </Text>
    </Group>
  )
}

interface FormState {
  id: number | null
  tenant_id: number | null
  recipients: string[]
  frequency: FinopsReportFrequency
  format: FinopsReportFormat
  hour_brt: number
  enabled: boolean
}

const EMPTY_FORM: FormState = {
  id: null,
  tenant_id: null,
  recipients: [],
  frequency: 'weekly',
  format: 'pdf',
  hour_brt: 8,
  enabled: true,
}

/**
 * Página `/finops/relatorios/assinaturas`.
 *
 * Lista as assinaturas de relatório executivo (agendado/recorrente) do escopo
 * do caller, com o status da última execução de cada uma; permite criar,
 * editar, remover e "Enviar agora". Gated pela flag
 * `finops_reports_schedule_enabled`; mutações exigem a capability
 * `finops:export`.
 */
export function RelatorioAssinaturas() {
  const flags = useFeatureFlags()
  const can = useCan()
  const qc = useQueryClient()
  const tenants = useUserTenants()
  const canExport = can('finops', 'export')

  const [modalOpen, setModalOpen] = useState(false)
  const [form, setForm] = useState<FormState>(EMPTY_FORM)

  const subsQuery = useQuery({
    queryKey: ['finops-report-subscriptions'],
    queryFn: listReportSubscriptions,
    enabled: flags.finops_reports_schedule_enabled,
    staleTime: 30_000,
  })

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['finops-report-subscriptions'] })
    qc.invalidateQueries({ queryKey: ['finops-sub-runs'] })
  }

  const onError = (e: unknown) =>
    notifications.show({ title: 'Erro', message: String(e), color: 'red' })

  const createMut = useMutation({
    mutationFn: () =>
      createReportSubscription({
        tenant_id: form.tenant_id,
        recipients: form.recipients,
        frequency: form.frequency,
        format: form.format,
        hour_brt: form.hour_brt,
        enabled: form.enabled,
      }),
    onSuccess: () => {
      notifications.show({
        title: 'Assinatura criada',
        message: 'O relatório será enviado conforme a agenda.',
        color: 'green',
      })
      setModalOpen(false)
      invalidate()
    },
    onError,
  })

  const patchMut = useMutation({
    mutationFn: () =>
      patchReportSubscription(form.id!, {
        recipients: form.recipients,
        frequency: form.frequency,
        format: form.format,
        hour_brt: form.hour_brt,
        enabled: form.enabled,
      }),
    onSuccess: () => {
      notifications.show({ title: 'Assinatura atualizada', message: 'Alterações salvas.', color: 'green' })
      setModalOpen(false)
      invalidate()
    },
    onError,
  })

  const deleteMut = useMutation({
    mutationFn: (id: number) => deleteReportSubscription(id),
    onSuccess: () => {
      notifications.show({ title: 'Assinatura removida', message: 'A agenda foi cancelada.', color: 'gray' })
      invalidate()
    },
    onError,
  })

  const sendNowMut = useMutation({
    mutationFn: (id: number) => sendReportNow(id),
    onSuccess: (res) => {
      notifications.show({
        title: 'Envio disparado',
        message:
          res.outcome === 'failed'
            ? `Falha: ${res.detail ?? 'erro desconhecido'}`
            : `Resultado: ${res.outcome ?? '—'} (${res.sent} destinatário(s)).`,
        color: res.outcome === 'failed' ? 'red' : 'blue',
      })
      invalidate()
    },
    onError,
  })

  const openCreate = () => {
    setForm(EMPTY_FORM)
    setModalOpen(true)
  }

  const openEdit = (s: FinopsReportSubscription) => {
    setForm({
      id: s.id,
      tenant_id: s.tenant_id,
      recipients: s.recipients,
      frequency: s.frequency,
      format: s.report_format,
      hour_brt: s.hour_brt,
      enabled: s.enabled,
    })
    setModalOpen(true)
  }

  const onSubmit = () => {
    if (form.recipients.length < 1) {
      notifications.show({ title: 'Destinatários obrigatórios', message: 'Informe ao menos 1 e-mail.', color: 'red' })
      return
    }
    if (form.id == null) createMut.mutate()
    else patchMut.mutate()
  }

  // Opções de escopo: tenants do usuário + "Global" (admin:platform — o backend
  // re-valida; aqui oferecemos a opção e o servidor decide com 403 se não puder).
  const tenantOptions = [
    { value: 'global', label: 'Global (toda a organização)' },
    ...tenants.map((t) => ({ value: String(t.id), label: t.name })),
  ]

  // ── Flag OFF → tela some (404 no backend de qualquer forma) ──────────────
  if (!flags.finops_reports_schedule_enabled) {
    return (
      <Stack>
        <Title order={3}>Assinaturas de relatório</Title>
        <Alert color="gray">
          O agendamento de relatórios executivos não está disponível neste ambiente.
        </Alert>
      </Stack>
    )
  }

  // ── Sem permissão de leitura → mensagem (no_access) ──────────────────────
  if (!can('finops', 'read')) {
    return (
      <Stack>
        <Title order={3}>Assinaturas de relatório</Title>
        <Alert color="yellow">
          Você não tem acesso às assinaturas de relatório FinOps.
        </Alert>
      </Stack>
    )
  }

  const items = subsQuery.data ?? []

  return (
    <Stack>
      <Group justify="space-between">
        <div>
          <Title order={3}>Assinaturas de relatório</Title>
          <Text c="dimmed" size="sm">
            Relatórios executivos enviados automaticamente por e-mail (semanal/mensal).
          </Text>
        </div>
        <Tooltip
          label="Requer a permissão de exportação (finops:export)"
          disabled={canExport}
        >
          <Button
            leftSection={<IconPlus size={14} />}
            onClick={openCreate}
            disabled={!canExport}
          >
            Nova assinatura
          </Button>
        </Tooltip>
      </Group>

      {subsQuery.isLoading && <Skeleton height={200} />}
      {subsQuery.error && (
        <Alert color="red">Erro ao carregar as assinaturas. Tente novamente.</Alert>
      )}

      {!subsQuery.isLoading && !subsQuery.error && items.length === 0 && (
        <Alert color="blue" icon={<IconMailFast size={16} />}>
          Nenhuma assinatura cadastrada. Crie uma para receber o relatório por e-mail
          automaticamente.
        </Alert>
      )}

      {items.length > 0 && (
        <Table striped highlightOnHover withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Escopo</Table.Th>
              <Table.Th>Destinatários</Table.Th>
              <Table.Th>Frequência</Table.Th>
              <Table.Th>Formato</Table.Th>
              <Table.Th>Hora (BRT)</Table.Th>
              <Table.Th>Estado</Table.Th>
              <Table.Th>Última execução</Table.Th>
              <Table.Th ta="right">Ações</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {items.map((s) => (
              <Table.Tr key={s.id}>
                <Table.Td>
                  {s.tenant_id == null ? (
                    <Badge variant="light" color="grape">Global</Badge>
                  ) : (
                    <Text size="sm">
                      {tenants.find((t) => t.id === s.tenant_id)?.name ??
                        `Tenant ${s.tenant_id}`}
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <Text size="sm">
                    {s.recipients.length === 1
                      ? s.recipients[0]
                      : `${s.recipients.length} destinatários`}
                  </Text>
                </Table.Td>
                <Table.Td>{FREQUENCY_LABEL[s.frequency] ?? s.frequency}</Table.Td>
                <Table.Td>{FORMAT_LABEL[s.report_format] ?? s.report_format}</Table.Td>
                <Table.Td>{String(s.hour_brt).padStart(2, '0')}:00</Table.Td>
                <Table.Td>
                  <Badge variant="light" color={s.enabled ? 'green' : 'gray'}>
                    {s.enabled ? 'Ativa' : 'Pausada'}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <LastRunCell subscriptionId={s.id} />
                </Table.Td>
                <Table.Td>
                  <Group gap="xs" justify="flex-end" wrap="nowrap">
                    <Tooltip label="Enviar agora">
                      <ActionIcon
                        variant="light"
                        color="blue"
                        onClick={() => sendNowMut.mutate(s.id)}
                        loading={sendNowMut.isPending && sendNowMut.variables === s.id}
                        disabled={!canExport}
                        aria-label="Enviar agora"
                      >
                        <IconSend size={16} />
                      </ActionIcon>
                    </Tooltip>
                    <Tooltip label="Editar">
                      <ActionIcon
                        variant="light"
                        onClick={() => openEdit(s)}
                        disabled={!canExport}
                        aria-label="Editar"
                      >
                        <IconPencil size={16} />
                      </ActionIcon>
                    </Tooltip>
                    <Tooltip label="Remover">
                      <ActionIcon
                        variant="light"
                        color="red"
                        onClick={() => {
                          if (
                            window.confirm(
                              'Remover esta assinatura? A agenda será cancelada.',
                            )
                          )
                            deleteMut.mutate(s.id)
                        }}
                        loading={deleteMut.isPending && deleteMut.variables === s.id}
                        disabled={!canExport}
                        aria-label="Remover"
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={form.id == null ? 'Nova assinatura de relatório' : 'Editar assinatura'}
        centered
      >
        <Stack>
          {form.id == null && (
            <Select
              label="Escopo"
              description="Global cobre toda a organização (requer admin de plataforma)."
              data={tenantOptions}
              value={form.tenant_id == null ? 'global' : String(form.tenant_id)}
              onChange={(v) =>
                setForm((f) => ({
                  ...f,
                  tenant_id: v == null || v === 'global' ? null : Number(v),
                }))
              }
            />
          )}
          <TagsInput
            label="Destinatários (e-mails)"
            description="Pressione Enter para adicionar. 1 a 20 endereços."
            placeholder="ex.: financeiro@empresa.com"
            value={form.recipients}
            onChange={(v) => setForm((f) => ({ ...f, recipients: v.slice(0, 20) }))}
          />
          <Group grow>
            <Select
              label="Frequência"
              data={[
                { value: 'weekly', label: 'Semanal' },
                { value: 'monthly', label: 'Mensal' },
              ]}
              value={form.frequency}
              onChange={(v) =>
                v && setForm((f) => ({ ...f, frequency: v as FinopsReportFrequency }))
              }
            />
            <Select
              label="Formato"
              data={[
                { value: 'pdf', label: 'PDF' },
                { value: 'csv', label: 'CSV' },
                { value: 'xls', label: 'XLS' },
              ]}
              value={form.format}
              onChange={(v) =>
                v && setForm((f) => ({ ...f, format: v as FinopsReportFormat }))
              }
            />
          </Group>
          <NumberInput
            label="Hora de envio (BRT)"
            description="0 a 23 (horário de Brasília)."
            min={0}
            max={23}
            clampBehavior="strict"
            value={form.hour_brt}
            onChange={(v) =>
              setForm((f) => ({ ...f, hour_brt: typeof v === 'number' ? v : 8 }))
            }
          />
          <Switch
            label="Assinatura ativa"
            checked={form.enabled}
            onChange={(e) =>
              setForm((f) => ({ ...f, enabled: e.currentTarget.checked }))
            }
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={onSubmit}
              loading={createMut.isPending || patchMut.isPending}
              disabled={!canExport}
            >
              {form.id == null ? 'Criar' : 'Salvar'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  )
}
